import { useState, useEffect, useRef } from "react";

import { FaSearch } from "react-icons/fa";

import { useLocation, useNavigate } from "react-router-dom";

import Logo from "./Logo";
import NavLinks from "./NavLinks";
import UserMenu from "./UserMenu";
import IconButtons from "./IconButtons";
import AuthModal from "../AuthModal";
import MobileHeader from "./MobileHeader";
import api from "../../api";

export default function MainHeader() {
  const [isFixed, setIsFixed] = useState(false);
  const headerRef = useRef(null);
  const searchRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [showModal, setShowModal] = useState(false);

  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");

    if (!stored) {
      return null;
    }

    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error("Invalid user data:", error);
      localStorage.removeItem("user");
      return null;
    }
  });

  const isLoggedIn = !!user;

  const location = useLocation();
  const navigate = useNavigate();

  // =========================
  // SEARCH
  // =========================

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const handleSearch = (value) => {
    setSearch(value);

    if (!value.trim()) {
      setResults([]);
      setSearchLoading(false);
    }
  };

  // =========================
  // SEARCH API
  // DEBOUNCED
  // =========================

  useEffect(() => {
    const query = search.trim();

    if (!query) {
      setResults([]);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);

        const res = await api.get(
          `/api/products/search/?q=${encodeURIComponent(query)}`
        );

        if (cancelled) {
          return;
        }

        const data = Array.isArray(res.data)
          ? res.data
          : [];

        setResults(data);
      } catch (error) {
        if (!cancelled) {
          console.error("Search error:", error);
          setResults([]);
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  // =========================
  // GET PRODUCT IMAGE
  // =========================

  const getProductImage = (product) => {
    if (!product) {
      return "/placeholder.jpg";
    }

    // SUB IMAGES
    if (
      Array.isArray(product.sub_images) &&
      product.sub_images.length > 0
    ) {
      const firstImage = product.sub_images[0];

      if (typeof firstImage === "string") {
        return firstImage;
      }

      if (firstImage?.image) {
        return firstImage.image;
      }

      if (firstImage?.url) {
        return firstImage.url;
      }

      if (firstImage?.image_url) {
        return firstImage.image_url;
      }
    }

    // MAIN IMAGE
    if (product.image) {
      return product.image;
    }

    if (product.product_image) {
      return product.product_image;
    }

    if (product.main_image) {
      return product.main_image;
    }

    if (product.image_url) {
      return product.image_url;
    }

    return "/placeholder.jpg";
  };

  // =========================
  // SAFE PRICE
  // =========================

  const getSafePrice = (price) => {
    if (
      price === null ||
      price === undefined ||
      price === ""
    ) {
      return null;
    }

    const numericPrice = Number(price);

    if (!Number.isFinite(numericPrice)) {
      return null;
    }

    return numericPrice;
  };

  // =========================
  // GET PRODUCT PRICE
  // =========================

  const getProductPrice = (product) => {
    if (!product) {
      return null;
    }

    // SIZE PRICES FIRST
    if (
      Array.isArray(product.sizes) &&
      product.sizes.length > 0
    ) {
      const sizePrices = product.sizes
        .map((size) => getSafePrice(size?.price))
        .filter((price) => price !== null);

      if (sizePrices.length > 0) {
        return Math.min(...sizePrices);
      }
    }

    // NORMAL PRODUCT PRICE
    return getSafePrice(product.price);
  };

  // =========================
  // OPEN PRODUCT DETAIL
  // =========================

  const handleProductClick = (product) => {
    if (!product?.id) {
      console.error(
        "Product ID is missing:",
        product
      );
      return;
    }

    navigate(`/product/${product.id}`);

    setSearch("");
    setResults([]);
  };

  // =========================
  // CLOSE SEARCH OUTSIDE CLICK
  // =========================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setResults([]);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  // =========================
  // ESC CLOSE SEARCH
  // =========================

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setResults([]);
      }
    };

    window.addEventListener(
      "keydown",
      handleEsc
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleEsc
      );
    };
  }, []);

  // =========================
  // STICKY HEADER
  // =========================

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(
          headerRef.current.offsetHeight
        );
      }
    };

    updateHeaderHeight();

    const handleScroll = () => {
      setIsFixed(
        window.scrollY > headerHeight
      );
    };

    window.addEventListener(
      "scroll",
      handleScroll
    );

    window.addEventListener(
      "resize",
      updateHeaderHeight
    );

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );

      window.removeEventListener(
        "resize",
        updateHeaderHeight
      );
    };
  }, [headerHeight]);

  // =========================
  // RETURN
  // =========================

  return (
    <>
      {/* =========================
          DESKTOP HEADER
      ========================= */}

      <div className="hidden sm:block">
        {isFixed && (
          <div
            style={{
              height: headerHeight,
            }}
          />
        )}

        <header
          ref={headerRef}
          className={`
            w-full
            border-b
            border-[#E8DCCB]
            transition-all
            duration-300
            ${
              isFixed
                ? "fixed top-0 left-0 bg-[#F8F3EA]/95 backdrop-blur-md shadow-md z-50"
                : "relative bg-[#F8F3EA]"
            }
          `}
        >
          <div className="h-16 flex items-center justify-between px-8">

            {/* LOGO */}
            <Logo />

            {/* NAVIGATION */}
            <NavLinks location={location} />

            {/* SEARCH */}
            <div
              ref={searchRef}
              className="relative hidden lg:block"
            >
              <input
                type="text"
                value={search}
                onChange={(e) =>
                  handleSearch(e.target.value)
                }
                placeholder="Search products..."
                className="
                  w-72
                  border
                  border-[#D8C8B8]
                  rounded-full
                  py-2
                  pl-4
                  pr-10
                  text-sm
                  text-[#3B2A1F]
                  bg-white
                  placeholder:text-[#8A7664]
                  focus:outline-none
                  focus:ring-2
                  focus:ring-[#0F5C5C]
                  focus:border-[#0F5C5C]
                  transition
                "
              />

              <FaSearch
                className="
                  absolute
                  right-4
                  top-1/2
                  -translate-y-1/2
                  text-[#0F5C5C]
                "
              />

              {/* SEARCH LOADING */}
              {searchLoading &&
                search.trim() && (
                  <div
                    className="
                      absolute
                      top-12
                      left-0
                      w-full
                      bg-white
                      rounded-xl
                      shadow-xl
                      border
                      border-[#E8DCCB]
                      z-50
                      p-3
                      text-sm
                      text-[#6B5746]
                    "
                  >
                    Searching...
                  </div>
                )}

              {/* SEARCH RESULTS */}
              {!searchLoading &&
                results.length > 0 && (
                  <div
                    className="
                      absolute
                      top-12
                      left-0
                      w-full
                      bg-white
                      rounded-xl
                      shadow-xl
                      border
                      border-[#E8DCCB]
                      z-50
                      max-h-80
                      overflow-y-auto
                    "
                  >
                    {results.map((item) => {
                      const image =
                        getProductImage(item);

                      const price =
                        getProductPrice(item);

                      return (
                        <div
                          key={item.id}
                          onClick={() =>
                            handleProductClick(item)
                          }
                          className="
                            flex
                            items-center
                            gap-3
                            p-3
                            hover:bg-[#F8F3EA]
                            cursor-pointer
                            border-b
                            border-[#EEE4D8]
                            last:border-b-0
                            transition
                          "
                        >
                          {/* PRODUCT IMAGE */}
                          <img
                            src={image}
                            alt={
                              item.name ||
                              "Product"
                            }
                            className="
                              w-14
                              h-14
                              rounded-lg
                              object-cover
                              flex-shrink-0
                            "
                            onError={(event) => {
                              event.currentTarget.onerror =
                                null;

                              event.currentTarget.src =
                                "/placeholder.jpg";
                            }}
                          />

                          {/* PRODUCT INFO */}
                          <div className="min-w-0 flex-1">
                            <p
                              className="
                                font-semibold
                                text-sm
                                truncate
                                text-[#3B2A1F]
                              "
                            >
                              {item.name}
                            </p>

                            {/* PRICE */}
                            {price !== null ? (
                              <p
                                className="
                                  text-[#0F5C5C]
                                  font-bold
                                "
                              >
                                ${price.toFixed(2)}
                              </p>
                            ) : (
                              <p className="text-[#8A7664] text-sm">
                                Price unavailable
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

              {/* NO RESULTS */}
              {!searchLoading &&
                search.trim() &&
                results.length === 0 && (
                  <div
                    className="
                      absolute
                      top-12
                      left-0
                      w-full
                      bg-white
                      rounded-xl
                      shadow-xl
                      border
                      border-[#E8DCCB]
                      z-50
                      p-3
                      text-sm
                      text-[#6B5746]
                    "
                  >
                    No products found.
                  </div>
                )}
            </div>

            {/* ICONS */}
            <div
              className="
                flex
                items-center
                gap-4
                md:gap-6
                text-xl
                text-[#3B2A1F]
                relative
              "
            >
              <IconButtons />

              <UserMenu
                isLoggedIn={isLoggedIn}
                user={user}
                setUser={setUser}
                setShowModal={setShowModal}
              />
            </div>
          </div>
        </header>
      </div>

      {/* =========================
          MOBILE HEADER
      ========================= */}

      <div className="sm:hidden">
        <MobileHeader
          user={user}
          setUser={setUser}
          isLoggedIn={isLoggedIn}
          setShowModal={setShowModal}
          location={location}
        />
      </div>

      {/* =========================
          AUTH MODAL
      ========================= */}

      {showModal && (
        <AuthModal
          onClose={() =>
            setShowModal(false)
          }
          onLoginSuccess={(data) => {
            const userData = {
              pk: data.user.pk,
              username: data.user.username,
              email: data.user.email,
              first_name:
                data.user.first_name,
              last_name:
                data.user.last_name,
              role: data.role,
              access: data.access,
              refresh: data.refresh,
            };

            setUser(userData);

            localStorage.setItem(
              "user",
              JSON.stringify(userData)
            );

            setShowModal(false);
          }}
        />
      )}
    </>
  );
}